import { useCallback, useEffect, useState } from 'react';
import { Button, Spinner } from '@nextui-org/react';
import { apiFetch } from '../lib/api';

// Google Calendar de las reuniones con agencias (Agencias → Solicitudes).
// Un solo botón: «Iniciar sesión con Google». Las reuniones que reservan las
// agencias en «¿Tu agencia no aparece?» se crean en el calendario principal
// de esa cuenta, y sus horas ocupadas no se ofrecen en el formulario.
// Worker: cf-worker/src/api/agencyCalendar.js + lib/googleCalendar.js.

const ENDPOINT = '/api/backoffice/agency-calendar';

const GOOGLE_G = (
  <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.2-3.9 7.2-9.6 7.2-17z" />
    <path fill="#FBBC05" d="M10.6 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l8-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2.2 1.5-5.1 2.3-8.8 2.3-6.2 0-11.5-4.1-13.4-9.9l-8 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

export default function AgencyCalendarCard({ canWrite }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [authUrl, setAuthUrl] = useState('');

  const load = useCallback(async () => {
    try {
      setSt(await apiFetch(ENDPOINT, { cache: 'no-store' }));
    } catch (e) {
      setSt({ failed: true });
      setMessage(e.message || 'No se pudo leer el estado');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Al volver de la pestaña de Google (se cierra sola al conectar), refrescar.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  async function signIn() {
    // La pestaña se abre ya, dentro del clic: si se abriera tras la petición,
    // el navegador la bloquearía como ventana emergente.
    const tab = window.open('', 'tc-google-calendar', 'width=520,height=700');
    setBusy(true);
    setMessage('');
    setAuthUrl('');
    try {
      const { url } = await apiFetch(`${ENDPOINT}/connect`, { method: 'POST' });
      if (tab) tab.location.href = url;
      else setAuthUrl(url);
    } catch (e) {
      tab?.close();
      setMessage(e.message || 'No se pudo abrir el inicio de sesión de Google');
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!window.confirm('¿Desconectar Google Calendar? Las reuniones nuevas ya no se crearán en el calendario ni se tendrán en cuenta sus horas ocupadas.')) return;
    setBusy(true);
    setMessage('');
    try {
      setSt(await apiFetch(ENDPOINT, { method: 'DELETE' }));
    } catch (e) {
      setMessage(e.message || 'No se pudo desconectar');
    } finally {
      setBusy(false);
    }
  }

  const signInButton = (label) => (
    <Button variant="bordered" className="bg-white font-medium text-gray-800" startContent={GOOGLE_G} onPress={signIn} isLoading={busy} isDisabled={!canWrite || !st?.configured}>
      {label}
    </Button>
  );

  let body = null;
  if (!st) {
    body = <Spinner size="sm" />;
  } else if (st.failed) {
    body = null;
  } else if (!st.connected) {
    body = (
      <div className="flex flex-col items-start gap-2">
        {signInButton('Iniciar sesión con Google')}
        {!st.configured && <span className="text-xs text-default-500">Google aún no está activado en el servidor.</span>}
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col items-start gap-2">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span>Conectado como <span className="font-medium">{st.email || '—'}</span></span>
          <button className="text-danger hover:underline disabled:opacity-50" onClick={disconnect} disabled={!canWrite || busy}>Desconectar</button>
        </div>
        {st.error === 'expired' && (
          <>
            <span className="text-sm text-danger">Google ha cerrado la sesión: vuelve a iniciarla para seguir creando las reuniones.</span>
            {signInButton('Iniciar sesión con Google')}
          </>
        )}
        {st.error === 'google_error' && <span className="text-sm text-warning">Google no responde ahora mismo. Prueba en un rato.</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-default-200 p-4">
      <div>
        <h2 className="text-base font-semibold">Google Calendar</h2>
        <p className="text-sm text-default-500">
          Las reuniones que reservan las agencias se crean en tu calendario y las horas que ya tengas ocupadas no se ofrecen.
        </p>
      </div>
      {body}
      {authUrl && (
        <p className="text-sm">
          El navegador ha bloqueado la ventana de Google: <a className="text-primary hover:underline" href={authUrl} target="_blank" rel="noopener noreferrer">ábrela aquí</a>.
        </p>
      )}
      {message && <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{message}</div>}
    </div>
  );
}
